import http from '../http-common';

const API_URL_PATH = 'analysis-groups';

/** Drops undefined / null / '' entries so optional filters never reach the query string. */
function cleanParams(params = {}) {
  const query = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') query[key] = value;
  });
  return query;
}

function groupPath(groupId, suffix = '') {
  return `${API_URL_PATH}/${encodeURIComponent(groupId)}${suffix}`;
}

/** `{ source_type, source_id }` items with numeric ids (interview uses `interviews.id`). */
function normalizeItems(items) {
  return (Array.isArray(items) ? items : [])
    .filter((item) => item && item.source_type && item.source_id != null)
    .map((item) => ({ source_type: item.source_type, source_id: Number(item.source_id) }));
}

/**
 * Client for analysis groups (M4 contracts 2-8): named experiments (configuration version +
 * engines) applied to N videos, their members, group runs, metrics, snapshots and comparisons.
 *
 * Response shapes follow the Node contract: lists return `{ items, total }`, group runs return
 * 202 `{ batch, accepted: [{ source_type, source_id, run_id }], skipped: [{ ..., reason }] }`.
 */
class AnalysisGroupsService {
  /**
   * @param {{ limit?: number, search?: string }} params
   * @returns {Promise} resolves `{ items: GroupDTO[], total }`; each item carries counts
   *   `{ members, runs_by_status, last_batch, last_snapshot }`.
   */
  list(params = {}) {
    return http.get(API_URL_PATH, { params: cleanParams(params) });
  }

  /** @param {number|string} groupId */
  get(groupId) {
    return http.get(groupPath(groupId));
  }

  /**
   * @param {{ name: string, description?: string, config_id?: number|null,
   *   config_version_id?: string|null, engines: string[], threshold_pct?: number|null,
   *   frozen?: boolean }} payload
   */
  create(payload) {
    return http.post(API_URL_PATH, payload);
  }

  /**
   * Once frozen only name / description / threshold_pct are editable (server enforced).
   * @param {number|string} groupId
   * @param {{ name?: string, description?: string, threshold_pct?: number|null,
   *   engines?: string[], config_id?: number|null, config_version_id?: string|null }} payload
   */
  update(groupId, payload) {
    return http.put(groupPath(groupId), payload);
  }

  /** 409 while the group has non-terminal runs; runs and snapshots survive the deletion. */
  remove(groupId) {
    return http.delete(groupPath(groupId));
  }

  /** Freezes configuration and engines (also happens automatically on the first run). */
  freeze(groupId) {
    return http.post(groupPath(groupId, '/freeze'));
  }

  /**
   * @returns {Promise} resolves `{ items: [{ member_id, source_type, source_id, name, label,
   *   pinned_run_id, resolved: RunResolution }] }`
   */
  members(groupId) {
    return http.get(groupPath(groupId, '/members'));
  }

  /** @param {Array<{ source_type: 'interview'|'manual_analysis', source_id: number }>} items */
  addMembers(groupId, items) {
    return http.post(groupPath(groupId, '/members'), { items: normalizeItems(items) });
  }

  /** Adds every manual analysis of a folder. */
  fromFolder(groupId, folderId) {
    return http.post(groupPath(groupId, '/members/from-folder'), { folder_id: folderId });
  }

  /** Adds several sources of one type. */
  fromSelection(groupId, sourceType, sourceIds) {
    return http.post(groupPath(groupId, '/members/from-selection'), {
      source_type: sourceType,
      source_ids: (Array.isArray(sourceIds) ? sourceIds : []).map((id) => Number(id)),
    });
  }

  removeMember(groupId, memberId) {
    return http.delete(groupPath(groupId, `/members/${encodeURIComponent(memberId)}`));
  }

  /**
   * Pins a SUCCEEDED run of this group as the member's evaluated run (`null` unpins).
   * @param {number|string} groupId
   * @param {number|string} memberId
   * @param {string|null} runId
   */
  pinMember(groupId, memberId, runId) {
    return http.put(groupPath(groupId, `/members/${encodeURIComponent(memberId)}/pin`), {
      run_id: runId || null,
    });
  }

  /**
   * One QUEUED run per member (or per selected member) with the group's configuration and
   * engines; never promotes and never touches the source's SQL status.
   * @param {number|string} groupId
   * @param {{ members?: number[], batch?: string, note?: string }} payload
   * @returns {Promise} 202 `{ batch, accepted, skipped }`
   */
  run(groupId, payload = {}) {
    const body = {};
    if (Array.isArray(payload.members) && payload.members.length) {
      body.members = payload.members.map((id) => Number(id));
    }
    if (typeof payload.batch === 'string' && payload.batch.trim())
      body.batch = payload.batch.trim();
    if (typeof payload.note === 'string' && payload.note.trim()) body.note = payload.note.trim();
    return http.post(groupPath(groupId, '/run'), body);
  }

  /** @param {{ batch?: string, status?: string }} params */
  runs(groupId, params = {}) {
    return http.get(groupPath(groupId, '/runs'), { params: cleanParams(params) });
  }

  /** @returns {Promise} resolves `[{ batch, started_at, counts_by_status, complete, snapshot_id }]` */
  batches(groupId) {
    return http.get(groupPath(groupId, '/batches'));
  }

  /**
   * Group metrics (same shape as `/video-incoherence/metrics` plus `scope` and buckets).
   * @param {{ threshold?: number }} params
   */
  metrics(groupId, params = {}) {
    return http.get(groupPath(groupId, '/metrics'), { params: cleanParams(params) });
  }

  gestureMetrics(groupId) {
    return http.get(groupPath(groupId, '/gesture-metrics'));
  }

  /** Snapshots ordered by `computed_at`, each with `series_key` and `series_break`. */
  snapshots(groupId) {
    return http.get(groupPath(groupId, '/snapshots'));
  }

  /** Manual snapshot of the current group metrics. */
  createSnapshot(groupId, payload = {}) {
    const body = {};
    if (typeof payload.note === 'string' && payload.note.trim()) body.note = payload.note.trim();
    if (Number.isFinite(payload.threshold)) body.threshold = payload.threshold;
    return http.post(groupPath(groupId, '/snapshots'), body);
  }

  /**
   * Two groups side by side over their common members.
   * @param {number|string} a
   * @param {number|string} b
   * @param {{ threshold?: number, common_only?: boolean }} params
   */
  compare(a, b, params = {}) {
    return http.get(`${API_URL_PATH}/compare`, {
      params: cleanParams({ a, b, common_only: true, ...params }),
    });
  }

  /**
   * Group membership of several sources of one type.
   * @param {'interview'|'manual_analysis'} sourceType
   * @param {Array<number|string>} sourceIds
   * @returns {Promise} resolves per source `{ groups: [{ group_id, name, resolution }] }`
   */
  membership(sourceType, sourceIds) {
    const ids = (Array.isArray(sourceIds) ? sourceIds : []).map((id) => String(id)).join(',');
    return http.get(`${API_URL_PATH}/membership`, {
      params: { source_type: sourceType, source_ids: ids },
    });
  }

  /**
   * Launches the given items in every selected group (one run per item and group).
   * @param {{ group_ids: number[], items: Array<{ source_type, source_id }>,
   *   add_missing_members?: boolean, batch?: string, note?: string }} payload
   * @returns {Promise} 202 `{ batch, accepted, skipped }`
   */
  runSelection(payload) {
    const body = {
      group_ids: (payload.group_ids || []).map((id) => Number(id)),
      items: normalizeItems(payload.items),
      add_missing_members: Boolean(payload.add_missing_members),
    };
    if (typeof payload.batch === 'string' && payload.batch.trim())
      body.batch = payload.batch.trim();
    if (typeof payload.note === 'string' && payload.note.trim()) body.note = payload.note.trim();
    return http.post(`${API_URL_PATH}/run-selection`, body);
  }

  /** Run ids pinned by any member (retention never deletes them). */
  pinnedRuns() {
    return http.get(`${API_URL_PATH}/pinned-runs`);
  }
}

export default new AnalysisGroupsService();
